import * as React from "react"
import { DraftComposer } from "@/components/inbox/DraftComposer"
import { ResearchPanel } from "@/components/inbox/ResearchPanel"
import { SendControls } from "@/components/inbox/SendControls"
import { ThreadPane } from "@/components/inbox/ThreadPane"
import type { SelectedReply } from "@/components/inbox/types"

type ThreadWorkspaceProps = {
  readonly reply: SelectedReply
  readonly clients: readonly { readonly id: number; readonly display_name: string }[]
  readonly classifications: readonly string[]
}

export function ThreadWorkspace({ reply, clients, classifications }: ThreadWorkspaceProps) {
  const [draftBodyHtml, setDraftBodyHtml] = React.useState(reply.active_draft?.body_html ?? "")

  // Reset the composed body when the thread or the draft identity changes.
  // Lifted VERBATIM from Inbox.tsx — keep all three parts of the key, and keep
  // this in the RENDER phase rather than a useEffect:
  //   · `revision` matters because a save or a redraft can produce new content
  //     under the SAME draft id; without it the editor keeps stale text.
  //   · render-phase means the reset lands in the same commit as the new reply,
  //     so SendControls never sees a frame where `bodyHtml` still belongs to the
  //     previous thread. An effect runs after paint and would open that window.
  // Both screens navigate with preserveState: true, so this component is not
  // remounted between threads and the state would otherwise carry over.
  const draftResetKey = `${reply.id}:${reply.active_draft?.id ?? ""}:${reply.active_draft?.revision ?? ""}`
  const draftResetKeyRef = React.useRef(draftResetKey)
  if (draftResetKeyRef.current !== draftResetKey) {
    draftResetKeyRef.current = draftResetKey
    setDraftBodyHtml(reply.active_draft?.body_html ?? "")
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <ThreadPane reply={reply} clients={clients} classifications={classifications} />
      <ResearchPanel reply={reply} />
      <DraftComposer
        reply={reply}
        bodyHtml={draftBodyHtml}
        onBodyHtmlChange={setDraftBodyHtml}
      />
      <section className="overflow-hidden rounded-lg border border-hairline bg-page">
        <div data-send-button-wrapper>
          <SendControls reply={reply} bodyHtml={draftBodyHtml} />
        </div>
      </section>
    </div>
  )
}
